import useAxios from "../../helpers/Hooks/useAxios";
import { useEffect, useState } from "react";
import { URLS } from "../../helpers/URLS";
import NoData from "../../loaders/NoData";
import SkeletonLoader from "../../loaders/Skeleton";
import ProjectsTable, { projectNameOf, projectManagers, isProjectRunning } from "../Common/ProjectsTable";
import ProjectCharts from "./ProjectCharts";
import { TextField, InputAdornment, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { matchesSearch } from "../../helpers/search";
import "../../assets/scss/tables.scss";

/* the project name is nested, so it is flattened onto each row before
   searching rather than reaching into the shape here */
const SEARCH_FIELDS = ['projectID', 'projectNameText', 'managerText'];

export default function ProjectReport() {
    const { fetchData } = useAxios();

    const [loadSkeleton, setLoadSkeleton] = useState(true);
    const [projectReports, setProjectReports] = useState([]);
    const [search, setSearch] = useState('');

    const getProjectReport = async () => {
        setLoadSkeleton(true);
        const resp = await fetchData({ url: URLS.getProjectTeamList, method: 'get' });
        if (resp.status === 200) {
            setProjectReports(resp.data || []);
        }
        setLoadSkeleton(false);
    }

    useEffect(() => {
        getProjectReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const searchResult = projectReports
        .map((project) => ({
            ...project,
            projectNameText: projectNameOf(project),
            managerText: projectManagers(project),
        }))
        .filter((project) => matchesSearch(project, search, SEARCH_FIELDS))
        /* running projects first — they are the ones being worked on. Within
           each group the order stays alphabetical so it does not shuffle. */
        .sort((a, b) => {
            const aRunning = isProjectRunning(a);
            const bRunning = isProjectRunning(b);
            if (aRunning !== bRunning) return aRunning ? -1 : 1;
            return String(a.projectNameText).localeCompare(String(b.projectNameText));
        });

    return (
        <div className="wh-table">
            <div className="wh-toolbar">
                <h4 className="title">Team Project Report</h4>

                <div className="wh-toolbar-controls">
                    <TextField
                        placeholder="Search project, ID or manager…"
                        size="small"
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="wh-search-field"
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                            endAdornment: search
                                ? <InputAdornment position="end">
                                    <IconButton size="small" className="wh-search-clear"
                                        aria-label="Clear search" onClick={() => setSearch('')}>
                                        <CloseRoundedIcon fontSize="small" />
                                    </IconButton>
                                </InputAdornment>
                                : null,
                        }}
                    />
                </div>
            </div>

            {!loadSkeleton && projectReports.length > 0 && (
                <ProjectCharts projects={projectReports} />
            )}

            {loadSkeleton
                ? <SkeletonLoader />
                : (searchResult.length > 0
                    ? <ProjectsTable projectList={searchResult} />
                    : <NoData />)
            }
        </div>
    )
}
